// src/pages/gene.codes/bloods/BloodAnalysisModal.tsx
import React, { useState, useEffect } from "react";
import { Drawer, Button, Typography } from "antd";
import { ArrowLeftOutlined, MenuOutlined } from "@ant-design/icons";

import { DetailDrawer, InlineLoading } from "../../../components/shared";

import BloodResultsOverview from "./BloodAnalysiscomponents/BloodResultsOverview";
import BloodTestResultsTable from "./BloodAnalysiscomponents/BloodTestResultsTable";
import BloodAnalysisSidebar from "./BloodAnalysiscomponents/BloodAnalysisSidebar";
import BloodAnalysisStateNotice from "./BloodAnalysiscomponents/BloodAnalysisStateNotice";

import { Test } from "../../../types/healthcare/TestTypes";
import { Patient } from "../../../types/healthcare/PatientTypes";
import { BloodTest, BloodTestResult, BloodTestAnalysis } from "../../../types/lab/BloodTestTypes";
import { fetchBloodTest, fetchPatientBloodTests } from "../../../api/healthcare/Testing/BloodTestApi";
import { bloodTestFromOrder } from "../../../utils/geneCodes/bloodTestShape";
import { buildBloodWheelData } from "../../../utils/geneCodes/buildBloodWheelData";
import { useBloodRangeContext } from "../../../hooks/useBloodRangeContext";

import { useNotifications } from "../../../context/NotificationContext";

const { Text } = Typography;

interface BloodAnalysisModalProps {
	isOpen: boolean;
	onClose: () => void;
	test: Test;
	patient?: Patient;
	alltests?: Test[];
}

type ViewMode = "overview" | "results" | "analysis";

const BloodAnalysisModal: React.FC<BloodAnalysisModalProps> = ({ isOpen, onClose, test, patient, alltests }) => {
	const [viewMode, setViewMode] = useState<ViewMode>("overview");
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [selectedResult, setSelectedResult] = useState<BloodTestResult | null>(null);
	const [sidebarVisible, setSidebarVisible] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	const [bloodTest, setBloodTest] = useState<BloodTest | null>(null);
	const [analysis, setAnalysis] = useState<BloodTestAnalysis | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const { showError } = useNotifications();

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	useEffect(() => {
		if (!isOpen) return;

		const preloaded = bloodTestFromOrder(test as any);

		if (preloaded) {
			setBloodTest(preloaded);
			setAnalysis(preloaded.analysis ?? null);
			setViewMode("overview");
			setSelectedCategory(null);
			setSelectedResult(null);
			setSidebarVisible(false);
			return;
		}

		// Not pre-loaded — fetch from Healthcare backend
		const loadBloodTest = async () => {
			setIsLoading(true);
			setBloodTest(null);
			setAnalysis(null);
			try {
				let fetched: BloodTest | null = null;

				if (test?.blood_test_id) {
					// Preferred: direct fetch by FK
					fetched = await fetchBloodTest(test.blood_test_id);
				} else if (test?.patient_id && test?.sample_id) {
					// Fallback: search patient blood tests by source_sample_id
					const all = await fetchPatientBloodTests(test.patient_id);
					fetched = all.find((bt) => bt.source_sample_id === test.sample_id) ?? null;
				}

				setBloodTest(fetched);
				setAnalysis(fetched?.analysis ?? null);
			} catch {
				showError("Could not load blood test results.");
			} finally {
				setIsLoading(false);
			}
			setViewMode("overview");
			setSelectedCategory(null);
			setSelectedResult(null);
			setSidebarVisible(false);
		};

		loadBloodTest();
	}, [isOpen, test]);

	const transitionToViewMode = async (newMode: ViewMode) => {
		setIsTransitioning(true);
		await new Promise((resolve) => setTimeout(resolve, 200));
		setViewMode(newMode);
		setIsTransitioning(false);
	};

	const handleCategoryClick = async (category: string) => {
		setSelectedCategory(category);
		setSelectedResult(null);
		await transitionToViewMode("results");
	};

	const handleResultClick = (result: BloodTestResult) => {
		setSelectedResult(result);
		if (isMobile) {
			setSidebarVisible(true);
		}
	};

	const handleBackToOverview = async () => {
		setSelectedCategory(null);
		setSelectedResult(null);
		await transitionToViewMode("overview");
	};

	// One catalogue + patient context for every surface in the drawer, so the drill-down
	// buckets match the wheel names and every band resolves for the same patient
	const { catalogue, rangeContext, isLoading: catalogueLoading } = useBloodRangeContext(patient);

	const getResultsByCategory = (category: string) => {
		if (!bloodTest?.results) return [];

		if (catalogue.length > 0) {
			const bucket = buildBloodWheelData(bloodTest.results, catalogue).find((c) => c.name === category);
			return bucket ? bucket.results : [];
		}

		return bloodTest.results.filter((r) => (r.test_category || "Other / Ungrouped") === category).sort((a, b) => a.sort_order - b.sort_order);
	};

	const patientInfo = patient ? `${patient.first_name} ${patient.last_name}` : "Unknown Patient";
	const testInfo = "Blood Test — " + (test.test_date || "");

	// This modal only ever opens for a blood-test order, so once the fetch has settled
	// a missing BloodTest row means the async OCR+parse pipeline hasn't created it yet —
	// not that results will never exist. Treat that the same as a fresh 'processing'
	// analysis rather than "not available", so both the main content and the sidebar
	// (which independently render their own not-available/processing notice) agree.
	// A real row always wins, so an imported 'not_analysed' collection is never masked.
	const effectiveAnalysis: BloodTestAnalysis | null =
		analysis ?? (!isLoading && !isTransitioning && !bloodTest ? ({ status: "processing" } as BloodTestAnalysis) : null);

	const renderMainContent = (): JSX.Element => {
		if (isLoading || isTransitioning || catalogueLoading) {
			return (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
					<InlineLoading message={isLoading || catalogueLoading ? "Loading blood test results..." : "Loading..."} />
				</div>
			);
		}

		if (!bloodTest) {
			return (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
					<BloodAnalysisStateNotice analysis={effectiveAnalysis} />
				</div>
			);
		}

		switch (viewMode) {
			case "overview":
				return (
					<div style={{ height: "100%", overflowY: "auto" }}>
						<BloodResultsOverview bloodTest={bloodTest} analysis={effectiveAnalysis} catalogue={catalogue} rangeContext={rangeContext} onCategoryClick={handleCategoryClick} onResultClick={handleResultClick} selectedResult={selectedResult} />
					</div>
				);
			case "results":
				return (
					<div style={{ height: "100%", overflowY: "auto" }}>
						<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
							<Button icon={<ArrowLeftOutlined />} onClick={handleBackToOverview} type="text" size="small" />
							<Text style={{ fontWeight: 600, fontSize: 13 }}>{selectedCategory} Results</Text>
						</div>
						<BloodTestResultsTable
							results={selectedCategory ? getResultsByCategory(selectedCategory) : bloodTest.results || []}
							category={selectedCategory}
							rangeContext={rangeContext}
							onResultClick={handleResultClick}
							selectedResult={selectedResult}
							allTests={alltests}
						/>
					</div>
				);
			default:
				return renderMainContent();
		}
	};

	const renderSidebar = () => {
		return <BloodAnalysisSidebar analysis={effectiveAnalysis} bloodTest={bloodTest} selectedCategory={selectedCategory} selectedResult={selectedResult} setSeletecedResult={setSelectedResult} viewMode={viewMode} allTests={alltests || []} rangeContext={rangeContext} />;
	};

	return (
		<>
			<DetailDrawer
				open={isOpen}
				onClose={onClose}
				width="85vw"
				title={`${patientInfo} — ${testInfo}`}
				subtitle={patient ? `${patient.first_name} ${patient.last_name} · Blood Test` : "Blood Test"}
				headerActions={
					<>
						{viewMode !== "overview" && (
							<Button icon={<ArrowLeftOutlined />} onClick={handleBackToOverview} disabled={isTransitioning} size="small" type="text">
								{!isMobile && "Back"}
							</Button>
						)}
						{isMobile && analysis && (
							<Button icon={<MenuOutlined />} onClick={() => setSidebarVisible(!sidebarVisible)} size="small" type="text">
								Analysis
							</Button>
						)}
					</>
				}
				noPadding
			>
				<div
					style={{
						display: "flex",
						flexDirection: isMobile ? "column" : "row",
						width: "100%",
						height: "100%",
						overflow: "hidden",
						opacity: isTransitioning ? 0.7 : 1,
						transition: "opacity 0.3s",
					}}
				>
					<div
						style={{
							flex: 1,
							padding: isMobile ? 12 : 16,
							overflowY: "auto",
							height: "100%",
						}}
					>
						{renderMainContent()}
					</div>

					{!isMobile && (
						<div
							style={{
								width: 360,
								minWidth: 360,
								borderLeft: "1px solid var(--mg-border)",
								overflowY: "auto",
								height: "100%",
							}}
						>
							{renderSidebar()}
						</div>
					)}
				</div>
			</DetailDrawer>

			{isMobile && (
				<Drawer
					open={sidebarVisible}
					onClose={() => setSidebarVisible(false)}
					placement="bottom"
					height="80vh"
					title="Analysis"
					destroyOnClose={true}
					styles={{
						body: { padding: 0 },
					}}
				>
					{renderSidebar()}
				</Drawer>
			)}
		</>
	);
};

export default BloodAnalysisModal;
